/**
 * Kelley SD - Auth Context Provider
 *
 * React context that wraps the application in Supabase auth state.
 * Listens for auth state changes, exposes user/session, and
 * provides sign-in / sign-up / sign-out methods.
 *
 * Usage:
 *   <AuthProvider>
 *     <App />
 *   </AuthProvider>
 *
 *   const { user, signIn, signOut } = useAuth();
 *
 * @version 1.0.0 - Spec 014 Layer 1, Spec B
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase-browser';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Result shape returned by auth mutation methods. */
interface AuthResult {
  error: AuthError | null;
}

/** Value exposed through the AuthContext. */
interface AuthContextValue {
  /** The currently authenticated Supabase user, or null. */
  user: User | null;
  /** The current Supabase session, or null. */
  session: Session | null;
  /** True while the initial session is being resolved. */
  loading: boolean;

  /**
   * Sign in with email + password.
   * Returns an error if the credentials are invalid.
   */
  signIn: (email: string, password: string) => Promise<AuthResult>;

  /**
   * Create a new account with email, password, and display name.
   * The name is stored in `user_metadata.full_name`.
   */
  signUp: (
    email: string,
    password: string,
    name: string,
    /**
     * Which page the signup came from, e.g. 'sales'. Stored in Supabase user
     * metadata as `signup_source` so a signup can be attributed to the landing
     * page that produced it. Omitted entirely when not supplied, so existing
     * callers are unaffected.
     */
    signupSource?: string,
  ) => Promise<AuthResult>;

  /** Sign out and clear the local session. */
  signOut: () => Promise<AuthResult>;

  /**
   * Initiate Google OAuth sign-in.
   * Redirects the browser to Google, then back to the app.
   */
  signInWithGoogle: () => Promise<AuthResult>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // -----------------------------------------------------------------------
  // Bootstrap: resolve the existing session on mount and subscribe to
  // auth state changes for the lifetime of the provider.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    // 1. Get the current session (may exist from a previous page load).
    // Add timeout to prevent infinite loading on slow mobile connections.
    const timeout = setTimeout(() => {
      setLoading(false); // Unblock UI even if Supabase is unreachable
    }, 5000);

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      clearTimeout(timeout);
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
    }).catch(() => {
      clearTimeout(timeout);
      setLoading(false);
    });

    // 2. Listen for future auth state changes (sign-in, sign-out, token refresh).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // -----------------------------------------------------------------------
  // Auth methods
  // -----------------------------------------------------------------------

  const noClient = { error: { message: 'Supabase not configured', name: 'AuthError', status: 0 } as AuthError };

  const signIn = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    if (!supabase) return noClient;
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      name: string,
      signupSource?: string,
    ): Promise<AuthResult> => {
      if (!supabase) return noClient;
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          // Only write `signup_source` when there is one, so accounts created
          // from the generic /cosmos/signup route don't carry an empty field.
          data: signupSource
            ? { full_name: name, signup_source: signupSource }
            : { full_name: name },
          emailRedirectTo: `${window.location.origin}/cosmos/auth/callback`,
        },
      });
      return { error };
    },
    []
  );

  const signOut = useCallback(async (): Promise<AuthResult> => {
    if (!supabase) return noClient;
    const { error } = await supabase.auth.signOut();
    return { error };
  }, []);

  const signInWithGoogle = useCallback(async (): Promise<AuthResult> => {
    if (!supabase) return noClient;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { error };
  }, []);

  // -----------------------------------------------------------------------
  // Memoised context value
  // -----------------------------------------------------------------------

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      loading,
      signIn,
      signUp,
      signOut,
      signInWithGoogle,
    }),
    [user, session, loading, signIn, signUp, signOut, signInWithGoogle]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the auth context from any component inside `<AuthProvider>`.
 *
 * @throws if used outside of an AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
